import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import invariant from 'tiny-invariant';
import { UserApi } from '../../generated/api';
import { Address } from '../../models/ClientModels';
import { isNotNull } from '../../utils/utils';
import {
  currentUserId,
  currentUserId$,
  skipForUnauthenticatedUsers,
} from '../auth/utils';
import { firestore } from '../firebase/firebaseAccess';
import { userPrivateProfileDoc } from '../firebase/refs/privateProfileDocRef';
import {
  docDataWithId,
  snapshotToDataWithId,
} from '../firebase/rxfireExtensions';

export class UserServiceHelper {
  constructor(private userSvc: UserApi) {}

  addresses$ = skipForUnauthenticatedUsers(
    uid =>
      docDataWithId(firestore().collection('privateProfiles').doc(uid)).pipe(
        map(value => {
          if (!value) {
            return {
              addresses: [],
              defaultAddress: undefined,
              extraAddresses: [],
            };
          }
          const addresses = [
            value.defaultAddress,
            ...(value.extraAddresses ?? []),
          ].filter(isNotNull);
          return {
            addresses,
            defaultAddress: value.defaultAddress ?? undefined,
            extraAddresses: value.extraAddresses ?? [],
          };
        }),
      ),
    {
      addresses: [],
      defaultAddress: undefined,
      extraAddresses: [],
    },
  );

  async addressMarkAsDefault(addressCompareHash: string): Promise<void> {
    const { defaultAddress, extraAddresses } = await firstValueFrom(
      this.addresses$,
    );

    const isOperatingDefaultAddress =
      addressCompareHash === defaultAddress?.compareHash;
    const addrPos = extraAddresses.findIndex(
      a => a.compareHash === addressCompareHash,
    );

    if (isOperatingDefaultAddress) return;
    if (addrPos < 0) return;

    const address = extraAddresses[addrPos]!;
    await this.userSvc.userApiControllerUpdateBuyerAddress({
      userId: currentUserId()!,
      updateBuyerAddressRequest: {
        address,
        asDefault: true,
        pos: isOperatingDefaultAddress ? undefined : addrPos,
      },
    });
  }

  async getDefaultAddress(): Promise<Address | undefined> {
    const snapshot = await userPrivateProfileDoc(currentUserId()!).get({
      source: 'server',
    });
    const privateProfile = snapshotToDataWithId(snapshot)!;
    return privateProfile.defaultAddress ?? undefined;
  }

  async addressAdd(address: Address): Promise<Address> {
    // https://github.com/popshoplive/functions/blob/a8a7adc088f3f12e385e462eae89ce85f2c0142e/packages/commons/biz/address.ts#L127
    const resp = await this.userSvc.userApiControllerUpdateBuyerAddress({
      userId: currentUserId()!,
      updateBuyerAddressRequest: {
        address,
        asDefault: true,
        pos: -1,
      },
    });
    invariant(
      resp.address != null,
      "'address' is expected in create address response",
    );
    return resp.address;
  }

  async addressUpdate(
    addressCompareHash: string,
    address: Address,
  ): Promise<Address> {
    const { defaultAddress, addresses } = await firstValueFrom(this.addresses$);

    const isOperatingDefaultAddress =
      addressCompareHash === defaultAddress?.compareHash;
    const addrPos = addresses.findIndex(
      a => a.compareHash === addressCompareHash,
    );

    if (!isOperatingDefaultAddress && addrPos < 0) {
      return this.addressAdd(address);
    }

    const resp = await this.userSvc.userApiControllerUpdateBuyerAddress({
      userId: currentUserId()!,
      updateBuyerAddressRequest: {
        address,
        asDefault: true,
        pos: isOperatingDefaultAddress ? undefined : addrPos,
      },
    });

    invariant(
      resp.address != null,
      "'address' is expected in update address response",
    );
    return resp.address;
  }

  async addressDelete(addressCompareHash: string): Promise<void> {
    const { defaultAddress, extraAddresses } = await firstValueFrom(
      this.addresses$,
    );

    const isOperatingDefaultAddress =
      addressCompareHash === defaultAddress?.compareHash;
    const addrPos = extraAddresses.findIndex(
      a => a.compareHash === addressCompareHash,
    );

    if (!isOperatingDefaultAddress && addrPos < 0) return;

    await this.userSvc.userApiControllerUpdateBuyerAddress({
      userId: currentUserId()!,
      updateBuyerAddressRequest: {
        deleteThis: true,
        pos: isOperatingDefaultAddress ? undefined : addrPos,
      },
    });
  }

  async reportRecentlyViewedSeller(sellerId: string): Promise<void> {
    const uid = await firstValueFrom(currentUserId$);

    if (uid && sellerId !== uid) {
      try {
        await this.userSvc.userApiControllerWatchSeller({
          userId: uid,
          watchSellerRequest: {
            sellerId: sellerId,
          },
        });
      } catch (e) {
        // in dev environment, profile of show's seller is not correctly
        // setup, and the api call will report "Must be a seller".
        console.error(e);
      }
    }
  }
}
